import { useEffect, useId, useMemo, useState } from "react";
import CloseIcon from "@mui/icons-material/Close";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import Autocomplete from "@mui/material/Autocomplete";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import FormLabel from "@mui/material/FormLabel";
import IconButton from "@mui/material/IconButton";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { DIALOG_CLOSE_BUTTON_SX } from "../../constants/dialogCloseButtonStyles";
import { useSnackbar } from "../../contexts/SnackbarContext";
import { useGuardedAsyncCallback } from "../../hooks/useGuardedAsyncCallback";
import {
  getManualAssessmentReportShareRecipients,
  shareManualAssessmentReport,
} from "../../services/manualAssessmentService";
import {
  bucketFreyrRecipientOptions,
  buildManualAssessmentReportShareRequest,
  hasShareRecipients,
  invalidShareEmailsError,
  partitionShareEmails,
  SHARE_SUCCESS_TOAST_MS,
  type FreyrRecipientOption,
  type ManualIaPdfShareTarget,
} from "../../utils/manualAssessmentReportShare";
import { withoutBackdropClose } from "../../utils/withoutBackdropClose";
import { ShareAssessmentRecipientsSelect } from "./ShareAssessmentRecipientsSelect";

const DIALOG_PAPER_SX = {
  width: 640,
  maxWidth: "calc(100vw - 32px)",
  borderRadius: "3px",
} as const;

const TITLE_WRAP_SX = {
  display: "flex",
  alignItems: "flex-start",
  justifyContent: "space-between",
  px: 3,
  pt: 2,
  pb: 1.5,
  pr: 1.5,
} as const;

const FORM_LABEL_SX = {
  color: "rgba(54, 65, 83, 1)",
  fontFamily: '"Nunito Sans", sans-serif',
  fontSize: "14px",
  lineHeight: "20px",
  fontWeight: 400,
  mb: 0.5,
} as const;

const FIELD_SX = {
  "& .MuiInputBase-root": {
    fontFamily: '"Nunito Sans", sans-serif',
    fontSize: 14,
  },
} as const;

const FOOTER_BUTTON_SX = {
  textTransform: "none" as const,
  borderRadius: "4px",
  fontFamily: '"Nunito Sans", sans-serif',
  fontSize: 14,
  lineHeight: "20px",
  fontWeight: 400,
  px: 1.5,
  py: 1,
  minWidth: 0,
  height: 36,
  boxShadow: "none",
} as const;

export type ShareManualIaPdfDialogVariant =
  | "assessmentCenter"
  | "regulationDetails";

export type ShareManualIaPdfDialogProps = Readonly<{
  open: boolean;
  target: ManualIaPdfShareTarget | null;
  onClose: () => void;
  /** Details always posts `sendEmailNotification: false`. Both surfaces have External Recipients. */
  variant?: ShareManualIaPdfDialogVariant;
}>;

export function ShareManualIaPdfDialog({
  open,
  target,
  onClose,
  variant = "assessmentCenter",
}: ShareManualIaPdfDialogProps) {
  const { showMessage } = useSnackbar();
  const isDetailsVariant = variant === "regulationDetails";
  const externalInputId = useId();
  const [freyrOptions, setFreyrOptions] = useState<FreyrRecipientOption[]>([]);
  const [optionsLoading, setOptionsLoading] = useState(false);
  const [selectedFreyr, setSelectedFreyr] = useState<FreyrRecipientOption[]>([]);
  const [externalRecipients, setExternalRecipients] = useState<string[]>([]);
  const [emailInput, setEmailInput] = useState("");
  const [emailError, setEmailError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    setSelectedFreyr([]);
    setExternalRecipients([]);
    setEmailInput("");
    setEmailError(null);
    setSubmitting(false);

    let cancelled = false;
    setOptionsLoading(true);
    void getManualAssessmentReportShareRecipients()
      .then((options) => {
        if (!cancelled) setFreyrOptions(options);
      })
      .catch(() => {
        if (!cancelled) {
          setFreyrOptions([]);
          showMessage("Failed to load Freyr recipients.", "error");
        }
      })
      .finally(() => {
        if (!cancelled) setOptionsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open, showMessage]);

  const assessmentLink = target?.assessmentLink.trim() ?? "";

  const commitEmailInput = (raw: string): boolean => {
    const value = raw.trim();
    if (!value) {
      setEmailError(null);
      return true;
    }
    const { valid, invalid } = partitionShareEmails(value);
    if (valid.length) {
      setExternalRecipients((prev) => {
        const seen = new Set(prev.map((email) => email.toLowerCase()));
        const next = [...prev];
        for (const email of valid) {
          const key = email.toLowerCase();
          if (seen.has(key)) continue;
          seen.add(key);
          next.push(email);
        }
        return next;
      });
    }
    const error = invalidShareEmailsError(invalid);
    setEmailError(error);
    setEmailInput(invalid.join(", "));
    return error === null;
  };

  const canSubmit = useMemo(() => {
    if (!target?.attachmentS3Uri) return false;
    if (emailError) return false;
    const pending = emailInput.trim();
    const { valid: pendingValid, invalid: pendingInvalid } = pending
      ? partitionShareEmails(pending)
      : { valid: [], invalid: [] };
    if (pendingInvalid.length > 0) return false;
    return hasShareRecipients(
      bucketFreyrRecipientOptions(selectedFreyr),
      [...externalRecipients, ...pendingValid],
    );
  }, [
    emailError,
    emailInput,
    externalRecipients,
    selectedFreyr,
    target?.attachmentS3Uri,
  ]);

  const handleClose = () => {
    if (submitting) return;
    onClose();
  };

  const handleCopyLink = useGuardedAsyncCallback(async () => {
    if (!assessmentLink) return;
    try {
      await navigator.clipboard.writeText(assessmentLink);
      showMessage("Link copied.", "success");
    } catch {
      showMessage("Failed to copy the link.", "error");
    }
  });

  const handleShare = useGuardedAsyncCallback(async () => {
    if (!target) return;
    const { valid, invalid } = partitionShareEmails(emailInput);
    if (invalid.length) {
      setEmailError(invalidShareEmailsError(invalid));
      return;
    }
    const pendingEmails = [...externalRecipients, ...valid];

    const payload = buildManualAssessmentReportShareRequest(
      target,
      bucketFreyrRecipientOptions(selectedFreyr),
      {
        externalRecipients: pendingEmails,
        sendEmailNotification: !isDetailsVariant,
      },
    );
    if (!hasShareRecipients(payload.freyrRecipients, payload.externalRecipients)) {
      showMessage("Add at least one recipient.", "error");
      return;
    }

    setSubmitting(true);
    try {
      const result = await shareManualAssessmentReport(payload);
      if (result.status && result.status !== "success") {
        showMessage(result.message || "Failed to share the report.", "error");
        return;
      }
      showMessage("Report shared successfully.", "success", SHARE_SUCCESS_TOAST_MS);
      onClose();
    } catch (error) {
      const msg =
        error instanceof Error ? error.message : "Failed to share the report.";
      showMessage(msg, "error");
    } finally {
      setSubmitting(false);
    }
  });

  return (
    <Dialog
      open={open}
      onClose={withoutBackdropClose(handleClose)}
      maxWidth={false}
      PaperProps={{
        sx: {
          ...DIALOG_PAPER_SX,
          minHeight: 520,
        },
      }}
      aria-labelledby="share-manual-ia-pdf-title"
    >
      <DialogTitle id="share-manual-ia-pdf-title" sx={TITLE_WRAP_SX}>
        <Box>
          <Typography
            sx={{
              fontFamily: '"Nunito Sans", sans-serif',
              fontWeight: 700,
              fontSize: 20,
              lineHeight: "25px",
              color: "#0F172A",
            }}
          >
            Share Assessment
          </Typography>
          <Typography
            sx={{
              fontFamily: '"Nunito Sans", sans-serif',
              fontWeight: 400,
              fontSize: 14,
              lineHeight: "20px",
              color: "#64748B",
            }}
          >
            Send a secure assessment link to selected users, groups, or
            distribution lists.
          </Typography>
        </Box>
        <IconButton
          aria-label="Close"
          onClick={handleClose}
          disabled={submitting}
          sx={{ ...DIALOG_CLOSE_BUTTON_SX, mt: 0.25 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent
        sx={{
          px: 3,
          pt: "16px !important",
          pb: 3,
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 2.5,
          }}
        >
          <ShareAssessmentRecipientsSelect
            label="Freyr Recipients"
            options={freyrOptions}
            value={selectedFreyr}
            loading={optionsLoading}
            disabled={submitting}
            onChange={setSelectedFreyr}
          />
          <Box>
            <FormLabel htmlFor={externalInputId} sx={FORM_LABEL_SX}>
                External Recipients
            </FormLabel>
            <Autocomplete
                id={externalInputId}
                multiple
                freeSolo
                options={[]}
                value={externalRecipients}
                disabled={submitting}
                inputValue={emailInput}
                onInputChange={(_event, value, reason) => {
                  if (reason === "input" && value.includes(",")) {
                    commitEmailInput(value);
                    return;
                  }
                  setEmailInput(value);
                  if (emailError) {
                    const { invalid } = partitionShareEmails(value);
                    if (invalid.length === 0) setEmailError(null);
                  }
                }}
                onChange={(_event, next) => {
                  const emails = next.filter(
                    (item): item is string => typeof item === "string",
                  );
                  const invalid = emails.filter(
                    (email) => partitionShareEmails(email).invalid.length > 0,
                  );
                  if (invalid.length) {
                    setEmailError(invalidShareEmailsError(invalid));
                    return;
                  }
                  setExternalRecipients(emails);
                  setEmailError(null);
                }}
                renderTags={(value, getTagProps) =>
                  value.map((email, index) => (
                    <Chip
                      {...getTagProps({ index })}
                      key={email}
                      size="small"
                      label={email}
                    />
                  ))
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Enter emails, separated by comma or Enter"
                    size="small"
                    sx={FIELD_SX}
                    error={Boolean(emailError)}
                    helperText={emailError ?? undefined}
                    onBlur={() => {
                      if (emailInput.trim()) commitEmailInput(emailInput);
                    }}
                    onKeyDown={(event) => {
                      if (event.key !== "Enter" && event.key !== ",") return;
                      event.preventDefault();
                      event.stopPropagation();
                      commitEmailInput(emailInput);
                    }}
                  />
                )}
              />
          </Box>
          <Box>
            <FormLabel htmlFor="share-assessment-link" sx={FORM_LABEL_SX}>
              Assessment Link
            </FormLabel>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <TextField
                id="share-assessment-link"
                value={assessmentLink}
                size="small"
                fullWidth
                sx={{
                  ...FIELD_SX,
                  "& .MuiInputBase-root": {
                    ...FIELD_SX["& .MuiInputBase-root"],
                    backgroundColor: "#F5F5F5",
                  },
                  "& .MuiInputBase-input": {
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  },
                }}
                InputProps={{ readOnly: true }}
              />
              <Button
                variant="outlined"
                onClick={() => {
                  void handleCopyLink();
                }}
                disabled={!assessmentLink || submitting}
                startIcon={<ContentCopyIcon />}
                sx={{
                  ...FOOTER_BUTTON_SX,
                  flexShrink: 0,
                  borderColor: "#155DFC",
                  color: "#155DFC",
                }}
              >
                Copy
              </Button>
            </Box>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions
        sx={{
          px: 3,
          pt: 2.5,
          pb: 2,
          gap: 1.5,
        }}
      >
        <Button
          variant="outlined"
          onClick={handleClose}
          disabled={submitting}
          sx={{
            ...FOOTER_BUTTON_SX,
            borderColor: "#155DFC",
            color: "#155DFC",
          }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={() => {
            void handleShare();
          }}
          disabled={!canSubmit || submitting}
          sx={{
            ...FOOTER_BUTTON_SX,
            bgcolor: "#155DFC",
            "&:hover": { bgcolor: "#0F4FD8" },
          }}
        >
          {submitting ? "Sharing…" : "Share"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
